/**
 * Tournament Types and Interfaces
 * For managing esports tournaments within organizations
 */

export type PrizePoolCurrency = 'USD' | 'CLP';

export const PRIZE_POOL_CURRENCY_LABELS: Record<PrizePoolCurrency, string> = {
  USD: 'USD — Dólar',
  CLP: 'CLP — Peso chileno',
};

/**
 * Formats a prize pool amount with the correct currency symbol and locale.
 * USD → "$1,000 USD" | CLP → "$1.000.000 CLP"
 */
export function formatPrizePool(
  amount: number | string | null | undefined,
  currency: PrizePoolCurrency | null | undefined = 'USD',
): string {
  const num = Number(amount);
  if (!amount || isNaN(num) || num === 0) return '—';
  const cur = currency ?? 'USD';
  if (cur === 'CLP') {
    return `$${Math.round(num).toLocaleString('es-CL')} CLP`;
  }
  return `$${num.toLocaleString('en-US')} USD`;
}

export type TournamentFormat =
  | 'single_elimination'
  | 'double_elimination'
  | 'round_robin'
  | 'swiss'
  | 'group_stage';

export type TournamentStatus = 'draft' | 'registration' | 'in_progress' | 'completed' | 'cancelled';

export type GameType = 'valorant' | 'league_of_legends' | 'csgo' | 'dota2' | 'other';

export type MatchStatus = 'pending' | 'waiting' | 'in_progress' | 'completed' | 'walkover' | 'disputed';

export type TournamentRulesFormat = 'markdown' | 'pdf';

export interface TournamentSettings {
  requires_valorant_match_id?: boolean;
}

export interface TournamentSocialLinks {
  discord?: string;
  stream?: string;
  website?: string;
  bracket_link?: string;
}

export interface PrizeDistributionEntry {
  position: number;
  amount: number;
  label?: string;
}

export interface Tournament {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  game_type: GameType;
  format: TournamentFormat;
  status: TournamentStatus;
  max_teams: number;
  current_teams: number;
  start_date: string;
  end_date?: string;
  registration_deadline?: string;
  prize_pool?: number;
  prize_pool_currency?: PrizePoolCurrency;
  rules?: string;
  rules_format?: TournamentRulesFormat;
  rules_pdf_url?: string;
  banner_url?: string;
  social_links?: TournamentSocialLinks;
  prize_distribution?: PrizeDistributionEntry[];
  is_public: boolean;
  settings?: TournamentSettings;
  created_by: string;
  created_at: string;
  updated_at: string;
  organization?: {
    id: string;
    name: string;
  };
}

export interface TournamentTeam {
  id: string;
  tournament_id: string;
  organization_id: string;
  user_id?: string;
  team_name: string;
  team_tag?: string;
  logo_url?: string; // Team logo URL
  roster_id?: string; // Link to existing roster
  captain_name: string;
  captain_email: string;
  seed?: number; // For seeding in brackets
  status: 'registered' | 'confirmed' | 'disqualified' | 'withdrawn';
  registered_at: string;
  updated_at: string;
  players?: TournamentTeamPlayer[];
  staff?: TournamentTeamStaff[];
}

export interface TournamentMatch {
  id: string;
  tournament_id: string;
  organization_id: string;
  round: number; // Round number (1, 2, 3, etc.)
  match_number: number; // Match number within the round
  bracket_position?: string; // Position in bracket (e.g., "winner_r1_m1", "loser_r1_m1")
  team1_id?: string;
  team2_id?: string;
  team1_score?: number;
  team2_score?: number;
  winner_id?: string;
  loser_id?: string;
  status: MatchStatus;
  scheduled_time?: string;
  completed_at?: string;
  best_of: number; // Best of 1, 3, 5, etc.
  map_details?: MatchMapDetail[]; // For games with multiple maps
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type ValorantTeamSide = 'Red' | 'Blue';

export interface MatchMapPlayerStat {
  henrik_puuid: string;
  riot_name: string;
  riot_tag: string;
  team_side: ValorantTeamSide;
  tournament_team_id: string;
  tournament_team_player_id: string | null;
  agent: string;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  acs: number;
  headshots: number;
  bodyshots: number;
  legshots: number;
  damage_made: number;
  damage_received: number;
  rounds_played?: number;
}

export interface MatchMapDetail {
  map_number: number;
  map_name: string;
  team1_score: number;
  team2_score: number;
  winner_id: string;
  valorant_match_id?: string;
  team1_side?: ValorantTeamSide;
  player_stats?: MatchMapPlayerStat[];
}

export interface MatchStatsPlayer {
  henrik_puuid: string;
  riot_name: string;
  riot_tag: string;
  team_side: string;
  tournament_team_id: string;
  tournament_team_player_id: string | null;
  agent: string;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  acs: number;
  headshots: number;
  bodyshots: number;
  legshots: number;
  damage_made: number;
  damage_received: number;
  rounds_played?: number | null;
  hs_pct: number;
}

export interface MatchStatsMapTeam {
  team_id: string;
  team_name: string;
  players: MatchStatsPlayer[];
}

export interface MatchStatsMap {
  map_number: number;
  map_name: string;
  team1_score: number;
  team2_score: number;
  winner_id: string | null;
  valorant_match_id?: string | null;
  teams: MatchStatsMapTeam[];
}

export interface MatchStatsResponse {
  match: Pick<
    TournamentMatch,
    | 'id'
    | 'round'
    | 'match_number'
    | 'team1_id'
    | 'team2_id'
    | 'team1_score'
    | 'team2_score'
    | 'winner_id'
    | 'status'
    | 'best_of'
    | 'completed_at'
  >;
  teams: Array<Pick<TournamentTeam, 'id' | 'team_name' | 'team_tag' | 'logo_url'>>;
  maps: MatchStatsMap[];
}

export function hasTournamentSocialLinks(links?: TournamentSocialLinks | null): boolean {
  if (!links) return false;
  return Boolean(links.discord || links.stream || links.website || links.bracket_link);
}

export function hasPrizeDistribution(
  distribution?: PrizeDistributionEntry[] | null,
): boolean {
  return Boolean(distribution?.some((entry) => entry.amount > 0));
}

export function getPrizePositionLabel(position: number, custom?: string): string {
  if (custom?.trim()) return custom.trim();
  const labels: Record<number, string> = {
    1: '1er lugar',
    2: '2do lugar',
    3: '3er lugar',
    4: '4to lugar',
  };
  return labels[position] ?? `${position}º lugar`;
}

export interface TournamentPlayerLeaderboardEntry {
  tournament_team_player_id: string;
  player_name: string;
  player_ign?: string;
  riot_id?: string;
  team_id: string;
  team_name: string;
  team_tag?: string;
  maps_played: number;
  total_kills: number;
  total_deaths: number;
  total_assists: number;
  kd_ratio: number;
  avg_acs: number;
  total_damage: number;
  avg_hs_pct: number;
}

export const VALORANT_MAPS = [
  'Ascent',
  'Bind',
  'Haven',
  'Lotus',
  'Pearl',
  'Split',
  'Sunset',
  'Abyss',
  'Icebox',
  'Fracture',
] as const;

export type ValorantMap = (typeof VALORANT_MAPS)[number];

export const BEST_OF_OPTIONS = [1, 3, 5] as const;

export function getBracketRoundCount(teamCount: number): number {
  const bracketSize = 2 ** Math.ceil(Math.log2(Math.max(teamCount, 2)));
  return Math.log2(bracketSize);
}

export function getBracketRoundLabels(teamCount: number): Array<{ round: number; label: string }> {
  const rounds = getBracketRoundCount(teamCount);
  const labels = ['Final', 'Semifinal', 'Cuartos', 'Octavos', 'Dieciseisavos', 'Treintaidosavos'];

  return Array.from({ length: rounds }, (_, index) => {
    const round = index + 1;
    const roundsFromFinal = rounds - round;
    return {
      round,
      label: labels[roundsFromFinal] ?? `Ronda ${round}`,
    };
  });
}

export function getDefaultBoConfig(teamCount: number): Record<number, number> {
  const rounds = getBracketRoundCount(teamCount);
  const config: Record<number, number> = {};

  for (let round = 1; round <= rounds; round += 1) {
    if (round === rounds) config[round] = 5;
    else if (round === rounds - 1) config[round] = 3;
    else config[round] = 1;
  }

  return config;
}

export function getSeriesWinsNeeded(bestOf: number): number {
  return Math.ceil(bestOf / 2);
}

export function calculateSeriesFromMaps(
  mapDetails: MatchMapDetail[],
  team1Id: string,
  team2Id: string,
  bestOf: number,
): {
  team1Score: number;
  team2Score: number;
  winnerId: string | null;
  isDecided: boolean;
} {
  let team1Score = 0;
  let team2Score = 0;

  for (const map of mapDetails) {
    if (!map.winner_id) continue;
    if (map.winner_id === team1Id) team1Score += 1;
    else if (map.winner_id === team2Id) team2Score += 1;
  }

  const winsNeeded = getSeriesWinsNeeded(bestOf);
  let winnerId: string | null = null;

  if (team1Score >= winsNeeded) winnerId = team1Id;
  else if (team2Score >= winsNeeded) winnerId = team2Id;

  return {
    team1Score,
    team2Score,
    winnerId,
    isDecided: winnerId !== null,
  };
}

export interface BracketNode {
  id: string;
  match: TournamentMatch;
  team1?: TournamentTeam;
  team2?: TournamentTeam;
  nextMatchId?: string;
  nextMatchPosition?: 'team1' | 'team2';
}

export interface TournamentBracket {
  tournament: Tournament;
  teams: TournamentTeam[];
  matches: TournamentMatch[];
  structure: {
    winners?: BracketNode[][];
    losers?: BracketNode[][];
    finals?: BracketNode[];
    groups?: { [groupName: string]: BracketNode[][] };
  };
}

export interface TournamentStats {
  tournament_id: string;
  total_matches: number;
  completed_matches: number;
  pending_matches: number;
  total_teams: number;
  active_teams: number;
}

export interface CreateTournamentInput {
  name: string;
  description?: string;
  game_type: GameType;
  format: TournamentFormat;
  max_teams: number;
  start_date: string;
  end_date?: string;
  registration_deadline?: string;
  prize_pool?: number;
  prize_pool_currency?: PrizePoolCurrency;
  rules?: string;
  rules_format?: TournamentRulesFormat;
  rules_pdf_url?: string;
  banner_url?: string;
  is_public: boolean;
}

export interface UpdateTournamentInput {
  name?: string;
  description?: string;
  status?: TournamentStatus;
  start_date?: string;
  end_date?: string;
  registration_deadline?: string;
  prize_pool?: number;
  prize_pool_currency?: PrizePoolCurrency;
  rules?: string;
  rules_format?: TournamentRulesFormat;
  rules_pdf_url?: string;
  banner_url?: string;
  is_public?: boolean;
}

export type StaffRole = 'coach' | 'assistant_coach' | 'ceo' | 'manager' | 'analyst' | 'other';

export interface TournamentTeamPlayer {
  id: string;
  tournament_team_id: string;
  player_name: string;
  player_email?: string;
  player_ign?: string; // In-Game Name
  player_role?: string; // Role in game (e.g., "top", "jungle", "mid", "bot", "support" for LoL)
  riot_id?: string; // Riot ID (for Valorant and League of Legends)
  discord_tag?: string; // Discord tag (e.g., "username#1234")
  position?: number; // Position/order in team (1-10)
  created_at: string;
  updated_at: string;
}

export interface TournamentTeamStaff {
  id: string;
  tournament_team_id: string;
  staff_name: string;
  staff_email?: string;
  staff_role: StaffRole;
  created_at: string;
  updated_at: string;
}

export interface RegisterTeamInput {
  tournament_id: string;
  team_name: string;
  team_tag?: string;
  logo_url?: string;
  roster_id?: string;
  captain_name: string;
  captain_email: string;
  players?: Omit<TournamentTeamPlayer, 'id' | 'tournament_team_id' | 'created_at' | 'updated_at'>[];
  staff?: Omit<TournamentTeamStaff, 'id' | 'tournament_team_id' | 'created_at' | 'updated_at'>[];
}

// Minimum players required per game type
export const GAME_MIN_PLAYERS: Record<GameType, number> = {
  valorant: 5,
  league_of_legends: 5,
  csgo: 5,
  dota2: 5,
  other: 1, // Default minimum
};

export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  coach: 'Entrenador',
  assistant_coach: 'Asistente de Entrenador',
  ceo: 'CEO',
  manager: 'Manager',
  analyst: 'Analista',
  other: 'Otro',
};

export interface UpdateMatchInput {
  team1_score?: number;
  team2_score?: number;
  winner_id?: string;
  status?: MatchStatus;
  scheduled_time?: string;
  map_details?: MatchMapDetail[];
  notes?: string;
}

export interface TournamentFilters {
  status?: TournamentStatus[];
  game_type?: GameType[];
  format?: TournamentFormat[];
  search?: string;
}

export const TOURNAMENT_FORMAT_LABELS: Record<TournamentFormat, string> = {
  single_elimination: 'Eliminación Simple',
  double_elimination: 'Eliminación Doble',
  round_robin: 'Round Robin',
  swiss: 'Sistema Suizo',
  group_stage: 'Fase de Grupos',
};

export const TOURNAMENT_STATUS_LABELS: Record<TournamentStatus, string> = {
  draft: 'Borrador',
  registration: 'Inscripción Abierta',
  in_progress: 'En Progreso',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

/** Estados que pueden mostrarse en páginas públicas (no borrador ni cancelado). */
export const PUBLIC_VISIBLE_TOURNAMENT_STATUSES: TournamentStatus[] = [
  'registration',
  'in_progress',
  'completed',
];

export function isPubliclyVisibleTournament(tournament: { status: TournamentStatus }): boolean {
  return PUBLIC_VISIBLE_TOURNAMENT_STATUSES.includes(tournament.status);
}

export const GAME_TYPE_LABELS: Record<GameType, string> = {
  valorant: 'VALORANT',
  league_of_legends: 'League of Legends',
  csgo: 'CS:GO',
  dota2: 'Dota 2',
  other: 'Otro',
};

export const MATCH_STATUS_LABELS: Record<MatchStatus, string> = {
  pending: 'Pendiente',
  waiting: 'En espera',
  in_progress: 'En Progreso',
  completed: 'Completado',
  walkover: 'W.O.',
  disputed: 'Disputado',
};
